import { useState } from 'react';
import { useSubmitContactUsForm, useSubmitContactUsFormPublic } from '@gonfalon/rest-api/queries/contactUs';

import type { MarketoForm } from './forms';
import { useInvisibleCaptcha } from './useInvisibleCaptcha';

/**
 * Field values keyed by the field key the form's `marketo-form-submission-config`
 * entry defines. Unknown keys are rejected server-side, so the keys here are
 * part of the form's contract with marketing ops — not the UI's field names.
 */
export type MarketingFormFields = Record<string, string | number | boolean | undefined>;

/**
 * Who is submitting, which decides the endpoint:
 *
 * - `member` — an authenticated member acting on their own account. Requires
 *   subscription-management access, so it only fits plan and billing surfaces.
 * - `visitor` — anonymous lead capture. Adds reCAPTCHA, a per-email rate limit,
 *   and the email blocklist, and needs no account permissions.
 */
export type MarketingFormAudience = 'member' | 'visitor';

export interface MarketingFormSubmitCallbacks {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface UseMarketingFormSubmitOptions {
  formId: MarketoForm;
  audience: MarketingFormAudience;
}

/**
 * Submits a Marketo form through the backend instead of the Marketo client
 * script, so the request is observable and the lead is recorded server-side.
 *
 * Callers own their own form state and field mapping; this owns the endpoint
 * choice, the referrer URL, and — for anonymous submissions — the captcha the
 * public endpoint requires.
 *
 * @example
 * ```tsx
 * const { submit, captcha, isPending, isError, isSuccess } = useMarketingFormSubmit({
 *   formId: MarketoForm.EnterpriseDemo,
 *   audience: 'visitor',
 * });
 *
 * const onSubmit = ({ value }: { value: FormData }) => {
 *   submit({ email: value.workEmail, firstName: value.firstName }, { onSuccess: trackSubmitted });
 * };
 *
 * return (
 *   <Form onSubmit={handleFormSubmit}>
 *     {fields}
 *     {captcha}
 *   </Form>
 * );
 * ```
 */
export function useMarketingFormSubmit({ formId, audience }: UseMarketingFormSubmitOptions) {
  // Both mutations are created on every render because hook order can't depend
  // on `audience`. An unused idle mutation costs nothing.
  const memberSubmit = useSubmitContactUsForm();
  const visitorSubmit = useSubmitContactUsFormPublic();

  // Only the anonymous endpoint takes a captcha token; the member endpoint
  // authenticates the caller instead.
  const { getToken, captcha, isEnabled: usesCaptcha } = useInvisibleCaptcha({ enabled: audience === 'visitor' });
  const [isRunningCaptcha, setIsRunningCaptcha] = useState(false);

  const mutation = audience === 'member' ? memberSubmit : visitorSubmit;

  const submit = (fields: MarketingFormFields, callbacks: MarketingFormSubmitCallbacks = {}) => {
    const referrerUrl = window.location.href;

    if (audience === 'member') {
      memberSubmit.mutate({ formId, body: { fields, referrerUrl } }, callbacks);
      return;
    }

    const send = (captchaToken?: string) =>
      visitorSubmit.mutate({ formId, body: { fields, referrerUrl, captcha: captchaToken } }, callbacks);

    // With no challenge to run, submit synchronously rather than bouncing
    // through a microtask and flickering the pending state on the way.
    if (!usesCaptcha) {
      send();
      return;
    }

    setIsRunningCaptcha(true);
    void getToken()
      // `getToken` resolves its own failures, so this is for anything
      // unexpected: treat it as a failed challenge instead of leaving the
      // button spinning with no way back.
      .catch(() => '')
      .then((captchaToken) => {
        setIsRunningCaptcha(false);
        send(captchaToken);
      });
  };

  return {
    submit,
    // The challenge runs before the request, so a submit still waiting on a
    // token is pending from the form's point of view.
    isPending: isRunningCaptcha || mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    /**
     * The invisible reCAPTCHA widget. Render it inside the form; it is `null`
     * when the form submits as a member or captcha is off.
     */
    captcha,
  };
}
